import React, { useEffect } from 'react';
import CardHeader from '../CardHeader';
import ArrowRightIcon from '../../assets/icons/ArrowRightIcon';
import CloseIcon from '../../assets/icons/CloseIcon';
import { ATLAS_STAGING_ENVS } from '../../helpers/environment.js';

/**
 * Staging-only interstitial asking which Atlas environment (POS Integration
 * vs Staging) to continue to. Rendered as a blocking overlay right before an
 * Atlas redirect; never shown on production builds (see shouldSelectAtlasEnv).
 * Closing (X, backdrop, or Escape) cancels the pending redirect.
 */
function SelectAtlasEnv({ onSelect, onClose }) {
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function onBackdropClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Select an Atlas environment"
      onClick={onBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="light-view-wrapper relative">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-1 cursor-pointer bg-transparent border-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-up-clr-primary rounded">
          <CloseIcon />
        </button>
        <CardHeader title="Select an environment" wrapperClass="pb-2" />
        <p className="text-sm text-up-clr-dark-grey">
          You are on a test build. Choose which Atlas environment you want to
          continue to.
        </p>
        <ul role="list" aria-label="Available environments">
          {ATLAS_STAGING_ENVS.map((env, idx, envArr) => {
            return (
              <li key={env.host}>
                <EnvCard env={env} onSelect={onSelect} />
                {idx < envArr.length - 1 && (
                  <div
                    aria-hidden="true"
                    className="border-t border-up-clr-neutral-grey md:-mx-8"
                  />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function EnvCard({ env, onSelect }) {
  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(env);
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${env.name}. ${env.desc}`}
      onClick={() => onSelect(env)}
      onKeyDown={handleKeyDown}
      className="p-1 my-5 flex items-center cursor-pointer transition-colors duration-500 text-up-clr-neutral-grey hover:text-up-clr-tinted-grey focus:outline-none focus-visible:ring-2 focus-visible:ring-up-clr-primary focus-visible:ring-offset-2 rounded">
      <div style={{ maxWidth: 340 }} className="mr-auto">
        <p aria-hidden="true" className="text-lg font-semibold text-up-clr-black leading-6">
          {env.name}
        </p>
        <p aria-hidden="true" className="text-sm text-up-clr-dark-grey">{env.desc}</p>
        <p aria-hidden="true" className="text-xs text-up-clr-light-grey mt-1">
          {new URL(env.url).host}
        </p>
      </div>
      <div aria-hidden="true" className="p-2 select-none">
        <ArrowRightIcon />
      </div>
    </div>
  );
}

export default SelectAtlasEnv;
